/**
 * imageOptim.js — helpers for the responsive image pipeline.
 *
 * The backend stores WebP/AVIF variants alongside every uploaded image at
 * predictable widths.  These helpers build ``srcset``/``sizes`` strings and
 * lightweight ``<picture>`` source lists for any ``/api/files/…`` URL.
 *
 * Static seed photos (``/assets/coach-photos/…``) and external URLs pass
 * through untouched — we only rewrite paths we know the backend can serve
 * variants for.
 */

// Must stay in lock-step with WIDTHS in /app/backend/image_optimizer.py
export const RESPONSIVE_WIDTHS = [480, 960, 1600];

// Media pipeline version. Appended as `iv=` to every /api/files derivative URL
// so a NEW (versioned) URL bypasses stale CDN entries created before the
// optimizer existed. Bump ONLY when the bytes/behaviour behind a derivative
// materially change — NOT on every deploy (we want long-lived cacheability).
export const IMAGE_VARIANT_VERSION = 2;

// Static seed photos under /assets/{coach,aviation,marine}-photos/ have
// pre-generated WebP derivatives named "<base>.w{width}.webp" (build-time optimizer).
const STATIC_PHOTO_RE = /\/assets\/(coach|aviation|marine)-photos\/[^?]+\.(jpe?g|png|webp)$/i;

function isStaticPhoto(src) {
  return typeof src === "string" && STATIC_PHOTO_RE.test(src);
}

function isApiFile(src) {
  if (!src || typeof src !== "string") return false;
  // Backend-served uploads: absolute (…/api/files/…) or relative (/api/files/…)
  return /\/api\/files\//.test(src);
}

function isOptimizableUrl(src) {
  return isApiFile(src) || isStaticPhoto(src);
}

function staticVariant(src, w) {
  // /assets/coach-photos/foo.jpg -> /assets/coach-photos/foo.w960.webp
  return `${src.replace(/\.(jpe?g|png|webp)$/i, "")}.w${w}.webp`;
}

// Current backend origin (no trailing slash).  /api/files is a BACKEND route,
// so this is always the correct host to serve uploaded media from.
function backendOrigin() {
  const u = process.env.REACT_APP_BACKEND_URL;
  return u ? u.replace(/\/+$/, "") : "";
}

/**
 * normalizeApiFileHost — re-anchor any ``…/api/files/…`` URL onto the CURRENT
 * backend origin.  Seller uploads are persisted as ABSOLUTE URLs that bake in
 * whatever host was live at upload time; when the app is later served from a
 * different host (e.g. a forked Preview) those baked hosts 404 and every image
 * breaks.  Rewriting to REACT_APP_BACKEND_URL fixes stale/relative hosts and is
 * a strict no-op in Production (stored host already == backend origin).
 */
export function normalizeApiFileHost(src) {
  if (typeof src !== "string") return src;
  const i = src.indexOf("/api/files/");
  if (i < 0) return src;
  const origin = backendOrigin();
  if (!origin) return src;
  return origin + src.slice(i);
}

function withWidth(src, w) {
  if (isStaticPhoto(src)) return staticVariant(src, w);
  const [base, query = ""] = normalizeApiFileHost(src).split("?");
  const params = new URLSearchParams(query);
  params.set("w", String(w));
  params.set("iv", String(IMAGE_VARIANT_VERSION));
  return `${base}?${params.toString()}`;
}

/**
 * Build the srcset attribute value for an optimizable image URL.  Widths
 * larger than ``maxWidth`` (when provided) are omitted so we don't ship
 * multi-megapixel bytes to a thumbnail tile.
 */
export function buildSrcSet(src, { maxWidth } = {}) {
  if (!isOptimizableUrl(src)) return undefined;
  const widths = maxWidth
    ? RESPONSIVE_WIDTHS.filter((w) => w <= maxWidth || w === RESPONSIVE_WIDTHS[0])
    : RESPONSIVE_WIDTHS;
  return widths.map((w) => `${withWidth(src, w)} ${w}w`).join(", ");
}

/**
 * Return the URL for a single explicit width derivative (e.g. w=1600 for the
 * lightbox current image, w=960 for bounded adjacent preloads).  Non-optimizable
 * URLs (external, placeholder) pass through untouched.
 */
export function variantSrc(src, w) {
  if (!isOptimizableUrl(src)) return src;
  return withWidth(src, w);
}

/**
 * Pick a sensible default ``src`` — the middle width for uploads, otherwise
 * the URL as-is.  Browsers that honour ``srcset`` will override this.
 */
export function defaultSrc(src) {
  if (!isOptimizableUrl(src)) return src;
  return withWidth(src, 960);
}

/**
 * ``imagesrcset`` string for ``<link rel="preload" as="image">`` — same
 * format as ``srcset`` but consumed by the preload spec.  Returns undefined
 * for non-optimizable URLs.
 */
export function preloadSrcSet(src, opts) {
  return buildSrcSet(src, opts);
}
